import { useState, type ReactNode } from 'react'
import { NavLink, Link } from 'react-router-dom'
import { MessageSquare, Plug, LogOut, ArrowLeft, Sun, Moon, Monitor } from 'lucide-react'
import { TestOpsCommandLogo } from '@ytest/testops-command'
import { AthenaLogo, ToucanLogo } from '@ytest/ui'
import clsx from 'clsx'
import { useAuthStore } from '../stores/authStore'
import { useTheme } from '../hooks/useTheme'

const YTEST_URL = import.meta.env.VITE_YTEST_URL || 'https://ytest.ai'
const ATHENA_URL = import.meta.env.VITE_ATHENA_URL || 'https://athena.ytest.ai'
const TOUCAN_URL = import.meta.env.VITE_TOUCAN_URL || 'https://toucan.ytest.ai'

const navItems = [
  { to: '/app/chat', label: 'Chat', icon: MessageSquare },
  { to: '/app/integrations', label: 'Integrations', icon: Plug },
]

// Cross-product navigation. Tokens are SSO-shared via the identity service, so
// SSO is carried by the httpOnly `auth_token` cookie set by identity-api
// on login. We never put the JWT in the URL — that would leak it to history,
// server logs, and Referer headers.
function buildCrossProductUrl(baseUrl: string, entryPath: string) {
  return `${baseUrl}${entryPath}`
}

// Render-prop pattern: each product has its own logo signature. Each entry
// also carries the product's brand chip palette so cross-product links are
// visually identifiable in either theme — Athena = purple, Toucan = orange.
// Theme-stable: the chip colours don't shift on light/dark toggle, so the
// logo identity stays consistent.
const otherProducts: Array<{
  label: string
  url: string
  entryPath: string
  renderLogo: (size: number) => React.ReactNode
  /** Tailwind classes for the chip wrapper (bg + ring tints). */
  chipClass: string
}> = [
  {
    label: 'Athena',
    url: ATHENA_URL,
    entryPath: '/app/dashboard',
    renderLogo: (size) => <AthenaLogo size={size} variant="icon" />,
    // White chip with subtle ring — colorful logos pop against it in both
    // themes. shadow-sm gives faint elevation in light; dropped in dark
    // (shadows don't render on dark canvases).
    chipClass: 'bg-white ring-foreground/10 shadow-sm dark:shadow-none',
  },
  {
    label: 'Toucan',
    url: TOUCAN_URL,
    entryPath: '/app/dashboard',
    renderLogo: (size) => <ToucanLogo size={size} />,
    chipClass: 'bg-white ring-foreground/10 shadow-sm dark:shadow-none',
  },
]

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuthStore()
  const [isExpanded, setIsExpanded] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const { preference, cyclePreference } = useTheme()
  const themeLabel =
    preference === 'system' ? 'System theme' : preference === 'dark' ? 'Dark mode' : 'Light mode'
  const ThemeIcon = preference === 'system' ? Monitor : preference === 'dark' ? Sun : Moon
  const nextLabel =
    preference === 'system' ? 'light' : preference === 'light' ? 'dark' : 'system'

  const handleLogout = () => {
    logout()
    window.location.href = `${YTEST_URL}/logout`
  }

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Sidebar — collapsed by default, expands on hover (matches Athena/Toucan) */}
      <aside
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
        className={clsx(
          'bg-sidebar border-r border-subtle flex flex-col flex-shrink-0 transition-all duration-300 ease-in-out',
          isExpanded ? 'w-64' : 'w-16',
        )}
      >
        {/* Logo */}
        <Link
          to="/app/chat"
          className={clsx(
            'h-16 flex items-center border-b border-subtle transition-all duration-300 hover:bg-foreground/[0.04]',
            isExpanded ? 'px-4' : 'justify-center',
          )}
        >
          <TestOpsCommandLogo size={32} className="flex-shrink-0" />
          <div
            className={clsx(
              'ml-3 flex flex-col leading-tight transition-opacity duration-300',
              isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
            )}
          >
            <span className="text-sm font-bold text-foreground whitespace-nowrap">
              TestOps Command
            </span>
            <span className="text-[10px] text-muted-foreground font-medium whitespace-nowrap">
              powered by ytest.ai
            </span>
          </div>
        </Link>

        {/* Navigation */}
        <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
          {/* Back to Products */}
          <a
            href={`${YTEST_URL}/products`}
            title={!isExpanded ? 'All Products' : undefined}
            className="flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground mb-2 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 flex-shrink-0" />
            <span
              className={clsx(
                'ml-3 whitespace-nowrap transition-opacity duration-300 flex-1',
                isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
              )}
            >
              All Products
            </span>
          </a>

          {/* In-product nav */}
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              title={!isExpanded ? label : undefined}
              className={({ isActive }) =>
                clsx(
                  'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                  isActive
                    ? 'bg-accent-soft text-accent-soft-foreground'
                    : 'text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground',
                )
              }
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span
                className={clsx(
                  'ml-3 whitespace-nowrap transition-opacity duration-300 flex-1',
                  isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
                )}
              >
                {label}
              </span>
            </NavLink>
          ))}

          {/* Other products — cross-product nav */}
          <div className="pt-4 mt-4 border-t border-subtle">
            {/* Always reserve the label's height so the chips below it
             * don't jump when the sidebar expands. Only opacity transitions.
             * In collapsed mode the empty space acts as a visual separator
             * between in-product nav and cross-product nav. */}
            <p
              className={clsx(
                'px-3 mb-2 h-4 font-mono text-[10px] font-semibold uppercase tracking-widest text-subtle-foreground transition-opacity duration-300',
                isExpanded ? 'opacity-100' : 'opacity-0',
              )}
            >
              // other products
            </p>
            {otherProducts.map(({ label, renderLogo, url, entryPath, chipClass }) => (
              <a
                key={label}
                href={buildCrossProductUrl(url, entryPath)}
                title={!isExpanded ? label : undefined}
                className="group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground transition-colors"
              >
                {/* Brand chip — each product has its own theme-stable
                 * tint (Athena purple, Toucan orange). Gives the logos
                 * contrast against any sidebar surface and ties the
                 * cross-product nav to each product's identity. */}
                <span
                  className={clsx(
                    'flex h-9 w-9 items-center justify-center flex-shrink-0 rounded-lg ring-1',
                    /* GPU-accelerated layer isolation — without translateZ,
                     * the chip got pushed down ~1px when the row's hover bg
                     * repainted (sub-pixel layout shift in flex). Forcing
                     * the chip onto its own composite layer means parent
                     * paints can't affect its position. */
                    chipClass,
                  )}
                  style={{ transform: 'translateZ(0)' }}
                >
                  {renderLogo(26)}
                </span>
                <span
                  className={clsx(
                    'ml-3 whitespace-nowrap transition-opacity duration-300 flex-1',
                    isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
                  )}
                >
                  {label}
                </span>
              </a>
            ))}
          </div>
        </nav>

        {/* User section */}
        <div className="p-2 border-t border-subtle">
          <div
            className={clsx(
              'flex items-center rounded-lg p-2 transition-colors',
              isExpanded ? 'hover:bg-foreground/[0.04]' : '',
            )}
          >
            <div className="h-8 w-8 rounded-full bg-accent-soft flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium text-accent-soft-foreground">
                {user?.full_name ? getInitials(user.full_name) : 'U'}
              </span>
            </div>
            <div
              className={clsx(
                'ml-3 flex-1 min-w-0 transition-opacity duration-300',
                isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
              )}
            >
              <p className="text-sm font-medium text-foreground truncate">
                {user?.full_name || 'User'}
              </p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>

          {/* Theme toggle — cycles system → light → dark. Same shape as Sign
           * Out so they stack cleanly. Icon reflects the user's *preference*,
           * not the resolved theme, so a 'system' user sees the Monitor icon
           * even though the rendered theme is light or dark. */}
          <button
            onClick={cyclePreference}
            title={!isExpanded ? `Theme: ${themeLabel} (click for ${nextLabel})` : undefined}
            aria-label={`Theme: ${themeLabel}. Click to switch to ${nextLabel}.`}
            className="w-full flex items-center px-3 py-2.5 mt-1 text-sm text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground rounded-lg transition-colors"
          >
            <ThemeIcon className="h-5 w-5 flex-shrink-0" />
            <span
              className={clsx(
                'ml-3 whitespace-nowrap transition-opacity duration-300',
                isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
              )}
            >
              {themeLabel}
            </span>
          </button>

          <button
            onClick={() => setShowLogoutConfirm(true)}
            title={!isExpanded ? 'Sign out' : undefined}
            className="w-full flex items-center px-3 py-2.5 mt-1 text-sm text-destructive-foreground hover:bg-destructive-soft rounded-lg transition-colors"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <span
              className={clsx(
                'ml-3 whitespace-nowrap transition-opacity duration-300',
                isExpanded ? 'opacity-100' : 'opacity-0 w-0 overflow-hidden',
              )}
            >
              Sign out
            </span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-hidden relative">{children}</main>

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowLogoutConfirm(false)}
          />
          <div className="relative bg-popover rounded-2xl shadow-xl p-6 w-full max-w-sm mx-4 border">
            <div className="text-center">
              <div className="mx-auto w-12 h-12 bg-destructive-soft rounded-full flex items-center justify-center mb-4">
                <LogOut className="h-6 w-6 text-destructive-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Sign out?</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Are you sure you want to sign out of your account?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-foreground bg-foreground/[0.06] hover:bg-foreground/[0.10] rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-destructive hover:opacity-90 rounded-xl transition-colors"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
